import { ChangeEvent, useEffect, useState } from 'react';

import { Stack, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { Page } from 'common/components/Page';
import { StyledBreadcrumbs } from 'common/components/StyledBreadcrumbs';
import { DEFAULT_TENANT,USER_GROUPS_REPORT_SLUG, USER_GROUPS_REPORT_TITLE } from 'common/constants/reports';
import { ApplicationSuite, AppSuiteTenant, UserGroup } from 'common/types/Entities';
import { useProcessReportGenerateButtonState } from 'features/reports/hooks/useProcessReportGenerateButtonState';

import { AppSuiteSearchableDropDown } from '../../../../common/components/AppSuiteSearchableDropDown';
import { TenantSearchableDropDown } from '../../../../common/components/TenantSearchableDropDown';
import { selectIsReportModalOpen,setReportModalOpen } from '../../reportPrintingModalSlice';
import { selectIsTenantDropDownDisabled,setTenantDropDownDisabled } from '../../tenantSearchableDropDownSlice';
import { ReportFooterButtons } from '../common/ReportFooterButtons';
import { ReportUserLimit } from '../common/ReportUsersLimit';
import { UserGroupsMultipleSelectDropDown } from '../common/UserGroupsMultipleSelectDropDown';

import { UserGroupsReportContent } from './UserGroupsReportContent';

export function UserGroupsReport() {
    const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
    const [userLimit, setUserLimit] = useState<string>('100');
    const [userLimitDisabled, setUserLimitDisabled] = useState<boolean>(false);
    const [appSuite, setAppSuite] = useState<ApplicationSuite | null>(null);
    const [tenant, setTenant] = useState<AppSuiteTenant | null>(null);

    const dispatch = useAppDispatch();
    const isReportModalOpen = useAppSelector(selectIsReportModalOpen);
    const isTenantSearchableDropDownDisabled = useAppSelector(selectIsTenantDropDownDisabled);

    const { disableGenerateButton } = useProcessReportGenerateButtonState(userGroups);

    useEffect(() => {
        if(appSuite?.id) {
            dispatch(setTenantDropDownDisabled(false));
            setTenant(DEFAULT_TENANT);
            setUserGroups([]);
        }
    }, [appSuite?.id]);

    useEffect(() => {
        dispatch(setTenantDropDownDisabled(true));
    }, []);

    const handleSetUserLimit = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        if (!value) {
            setUserLimit('');
        }
        if (/^[1-9]\d*$/.test(value)) {
            setUserLimit(event.target.value);
        }
    };

    const handleSetUserLimitDisabled = (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setUserLimit(checked ? 'All Users' : '100');
        setUserLimitDisabled(checked);
    };

    const handleOpenReportModal = () => {
        dispatch(setReportModalOpen(true));
    };

    const handleApplicationSuiteChange = (_e: unknown, newAppSuite: ApplicationSuite | null) => {
        setAppSuite(newAppSuite);
    };

    const handleTenantChange = (_e: unknown, newTenant: AppSuiteTenant | null) => {
        setTenant(newTenant);
        setUserGroups([]);
    };

    const _isAbleToGenerateReport = isReportModalOpen && tenant;

    return (
        <Page
            title={`${USER_GROUPS_REPORT_TITLE} Report`}
            header={(
                <Typography variant="h5" gutterBottom component="div">
                    <StyledBreadcrumbs
                        numberOfTabsToExcludeFromLinks={1}
                        guidNameMappings={[{
                            resourceName: USER_GROUPS_REPORT_TITLE,
                            resourceId: USER_GROUPS_REPORT_SLUG
                        }]}
                    />
                    {`${USER_GROUPS_REPORT_TITLE} Report`}
                </Typography>
            )}
        >
            <Stack
                sx={{ paddingTop: '1rem', width: '30%' }}
                spacing={2}
            >
                <AppSuiteSearchableDropDown
                    value={appSuite}
                    onChange={handleApplicationSuiteChange}
                />
                <TenantSearchableDropDown
                    appSuite={appSuite}
                    value={tenant ?? DEFAULT_TENANT}
                    onChange={handleTenantChange}
                    disabled={isTenantSearchableDropDownDisabled}
                />
                <UserGroupsMultipleSelectDropDown
                    value={userGroups}
                    onChange={setUserGroups}
                    tenantId={tenant?.id ?? ''}
                />
                <ReportUserLimit
                    userLimit={userLimit}
                    handleSetUserLimit={handleSetUserLimit}
                    handleSetUserLimitDisabled={handleSetUserLimitDisabled}
                    userLimitDisabled={userLimitDisabled}
                />
                <ReportFooterButtons
                    handleOpenReportModal={handleOpenReportModal}
                    disableGenerateButton={disableGenerateButton}
                />
            </Stack>
            {_isAbleToGenerateReport && <UserGroupsReportContent
                userGroups={userGroups}
                userLimit={userLimit}
                userLimitDisabled={userLimitDisabled}
                currentTenant={tenant}
            />}
        </Page>
    );
}
